import { useQuery } from "@tanstack/react-query";
import type { HouseholdBrief, HouseholdMember, HouseholdPetBrief } from "@/shared/contracts";
import { env } from "@/shared/config/env";
import { queryKeys } from "@/shared/query/queryClient";
import { useServices } from "@/shared/services/registry";
import { householdLabel, useCurrentHousehold, useHouseholdLabels } from "@/shared/session/householdContext";

/** 家庭群要的三样：群名、群里的伙伴（已入住的）、家人（拿不到是 null，不是空数组）。 */
export interface FamilyGroupView {
  householdName: string | null;
  pets: HouseholdPetBrief[];
  people: HouseholdMember[] | null;
}

/**
 * 纯函数：家庭简况＋（可选）称呼表＋（可选）成员 → 家庭群要的三样。
 * - 群名：与切换栏、「我们的家」同一套称呼（useHouseholdLabels，撞名带序号）；没有称呼表时退回 householdLabel（没起名＝「第一只的家」）；
 * - 伙伴：只取已入住的（join_step＝moved_in），顺序照家庭简况；
 * - 家人：成员接口拿到才有；拿不到（演示模式答「能力未接入」、还在读、读失败）一律 null，由界面只列伙伴，不报错。
 */
export function familyGroupView(household: HouseholdBrief | null, labels: Map<string, string> | null, members: HouseholdMember[] | null | undefined): FamilyGroupView {
  if (!household) return { householdName: null, pets: [], people: null };
  return {
    householdName: labels?.get(household.household_id) ?? householdLabel(household, 0),
    pets: household.pets.filter((pet) => pet.join_step === "moved_in"),
    people: members ?? null,
  };
}

/**
 * 通讯器「家庭群」取数（P2，6c2b 接线）：家庭上下文给家庭简况；成员读 households.detail，查询键与「我们的家」共用缓存
 * （queryKeys.householdDetail），启用条件同 HouseholdPage：演示模式照问演示服务（答能力未接入就当没有家人资料），live 要有账号和家庭。
 */
export function useFamilyGroup(): FamilyGroupView {
  const { households } = useServices();
  const { userId, household } = useCurrentHousehold();
  const labels = useHouseholdLabels(userId);
  const householdId = household?.household_id ?? "";
  const fixture = env.dataMode === "fixture";
  const detail = useQuery({
    queryKey: queryKeys.householdDetail(userId ?? "-", householdId),
    queryFn: ({ signal }) => households.detail(householdId, signal),
    enabled: fixture || Boolean(userId && householdId),
  });
  return familyGroupView(household, labels, detail.data?.members);
}
